import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowClockwise,
  ArrowRight,
  BellSlash,
  CalendarBlank,
  CheckCircle,
  ChartLine,
  CheckSquareOffset,
  ClipboardText,
  Coins,
  Eye,
  Funnel,
  Lightbulb,
  Receipt,
  Trophy,
  UserGear,
  Users,
  UsersThree,
  type Icon,
} from '@phosphor-icons/react';

import {
  ApproveNotImplementedError,
  approveSuggestion,
  listCheckSettings,
  listOpenSuggestions,
  stopWatchingSuggestion,
  triggerSweep,
  upsertCheckSetting,
  type CheckSetting,
  type Suggestion,
} from '@/api/automation';
import { Badge } from '@/components/ui/badge';
import { LoadError } from '@/components/LoadError';
import { ProposalsPanel } from '@/components/ProposalsPanel';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RowActions, type RowAction } from '@/components/ui/row-actions';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TableSkeleton } from '@/components/ui/table-skeleton';
import {
  TablePagination,
  useTablePaginationState,
} from '@/components/ui/table-pagination';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useConfirm } from '@/components/ConfirmDialog';
import { useToast } from '@/components/Toast';

// Categories are SERVER-owned (internal/services/automation/categories.go) and
// arrive on every suggestion / check as `category`. The SPA used to keep its own
// check_key → category map here; it was written when there were ~20 checks, was
// never updated as the registry grew past 35, and every newer check therefore
// rendered a raw untranslated "other" badge. A Go test now fails the build if a
// registered check has no category, so there is nothing left to keep in sync.
//
// An absent category (rule-origin suggestion, or a stored row naming a check key
// that no longer exists) renders NO badge rather than a placeholder.

// Filter options come from the categories the server actually returned, so a new
// backend category shows up in the dropdown with no SPA change at all.
function categoriesOf(checks: readonly { category?: string }[]): string[] {
  return Array.from(
    new Set(checks.map((c) => c.category).filter((c): c is string => !!c)),
  ).sort();
}

// Radix Select rejects "" as a value, so the "all categories" choice maps
// to a sentinel both directions (mirrors the convention in BillingInvoices).
const ALL_CATEGORIES_SENTINEL = '__all';

// Icon per category — keeps the filter dropdown scannable at a glance and
// matches the icon vocabulary the sidebar already uses (Users for members,
// UsersThree for teams, etc.). Funnel is the catch-all "filter" verb on the
// "All" option. New categories without an entry fall back to Funnel.
const CATEGORY_ICON: Record<string, Icon> = {
  members: Users,
  teams: UsersThree,
  schedule: CalendarBlank,
  billing: Receipt,
  administrators: UserGear,
  signups: ClipboardText,
  attendance: CheckSquareOffset,
  fixtures: Trophy,
  notices: Coins,
  pulse: ChartLine,
};

function CategoryIcon({ category, className }: { category: string; className?: string }) {
  const I = CATEGORY_ICON[category] ?? Funnel;
  return <I weight="bold" className={className ?? 'h-4 w-4'} />;
}

// Assistant — top-level page that lists what the nightly sweep noticed.
// Two tabs: Open (decisions waiting) + Rejected (restore source). Approve is
// stubbed in Phase 1: clicking it surfaces a toast pointing the user at the
// fact that the materialiser isn't live yet. The detail view is inlined per
// card here; we'll graduate to a dedicated route once blast_radius.detail
// payloads grow real ornaments worth a dedicated layout.
export function Assistant() {
  const { slug = '' } = useParams();
  const { t } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const toast = useToast();
  const navigate = useNavigate();

  // The category filter's options come from the checks the server returned, so
  // this reads the SAME query key the Settings tab renders from. React Query
  // dedupes by key, so sharing it costs no extra request — and it means the
  // dropdown can never offer a category the check list doesn't actually have.
  const checksForFilter = useQuery({
    queryKey: ['assistant-checks', slug],
    queryFn: () => listCheckSettings(token, slug),
    enabled: !!token && !!slug,
  });
  const filterCategories = useMemo(
    () => categoriesOf(checksForFilter.data ?? []),
    [checksForFilter.data],
  );

  const openQuery = useQuery({
    queryKey: ['assistant-suggestions', 'open', slug],
    queryFn: () => listOpenSuggestions(token, slug),
    enabled: !!token && !!slug,
    // Re-fetch every time the user lands on /assistant. Suggestions can
    // resolve out-of-band (a member-card edit, a teammate's fix in another
    // tab, or coming back from the detail page after a fix), and the
    // global 60s staleTime would otherwise keep showing the old list for
    // up to a minute. 'always' bypasses staleTime on mount only —
    // background polling is still off.
    refetchOnMount: 'always',
  });

  // Stop-watching disables the underlying check AND closes the open row, so
  // both the Open list and the Settings tab's check list need to refresh.
  const invalidateAssistant = () => {
    void queryClient.invalidateQueries({ queryKey: ['assistant-suggestions', 'open', slug] });
    void queryClient.invalidateQueries({ queryKey: ['assistant-checks', slug] });
  };

  const stopWatchingMutation = useMutation({
    mutationFn: (id: string) => stopWatchingSuggestion(token, slug, id),
    onSuccess: () => {
      toast.success(t('assistant.toast.stoppedWatching'));
      invalidateAssistant();
    },
    onError: () => toast.error(t('assistant.toast.stopWatchingFailed')),
  });

  const approveMutation = useMutation({
    mutationFn: (id: string) => approveSuggestion(token, slug, id),
    onError: (err) => {
      if (err instanceof ApproveNotImplementedError) {
        toast.success(t('assistant.actions.approveNotReady'));
        return;
      }
      toast.error(t('assistant.toast.stopWatchingFailed'));
    },
    onSuccess: () => {
      invalidateAssistant();
    },
  });

  // Tabs are controlled so the header can conditionally render the
  // category filter — only meaningful on Settings, would be a no-op visual
  // on the Open list.
  const [activeTab, setActiveTab] = useState<'open' | 'settings'>('open');
  // Filter state lives here (not inside ChecksSettings) so it survives
  // tab switches and so the dropdown can render in the page header row.
  const [categoryFilter, setCategoryFilter] = useState<string>(ALL_CATEGORIES_SENTINEL);

  const sweepMutation = useMutation({
    mutationFn: () => triggerSweep(token, slug),
    onSuccess: (res) => {
      // Open list AND check settings may shift — auto-resolve closes open
      // suggestions, and the sweep itself can raise new ones.
      invalidateAssistant();
      // Three-bucket tally so the user can see what the sweep actually did:
      // newly raised, already open, and auto-resolved (closed because the
      // condition no longer matches).
      toast.success(
        t('assistant.toast.sweepDone', {
          raised: res.findings_raised,
          skipped: res.findings_skipped,
          resolved: res.findings_auto_resolved,
        }),
      );
    },
    onError: () => toast.error(t('assistant.toast.sweepFailed')),
  });

  const handleStopWatching = async (id: string) => {
    const ok = await confirm({
      title: t('assistant.actions.stopWatchingConfirm'),
      body: t('assistant.actions.stopWatchingConfirmBody'),
      confirmLabel: t('assistant.actions.stopWatching'),
    });
    if (ok) stopWatchingMutation.mutate(id);
  };

  return (
    <section className="mx-auto w-full max-w-[var(--content-max)] px-4 py-6 sm:px-8 sm:py-8">
      <header className="mb-6 flex items-center gap-2">
        <Lightbulb weight="bold" className="h-6 w-6 text-primary" />
        <h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">
          {t('assistant.title')}
        </h1>
      </header>

      {/* Pending schedule proposals leaders sent from the app — review +
          approve here, with a conflict verdict before the button (plan W4b). */}
      <div className="mb-6">
        <ProposalsPanel slug={slug} token={token} />
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(v) => setActiveTab(v as 'open' | 'settings')}
        className="flex flex-col gap-5"
      >
        {/* Action bar — sits directly above the tabs. Subtitle is the
            left-hand context line; filter + Run button live on the right.
            Filter shows only on Settings (the only tab whose content the
            filter applies to); Run button is always visible because the
            sweep is page-level. */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-muted-foreground">{t('assistant.subtitle')}</p>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
          {activeTab === 'settings' && (
            <Select
              value={categoryFilter}
              onValueChange={setCategoryFilter}
            >
              <SelectTrigger className="w-auto min-w-[12rem]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_CATEGORIES_SENTINEL}>
                  <span className="flex items-center gap-2">
                    <Funnel weight="bold" className="h-4 w-4" />
                    {t('assistant.settings.filter.all')}
                  </span>
                </SelectItem>
                {filterCategories.map((cat) => (
                  <SelectItem key={cat} value={cat}>
                    <span className="flex items-center gap-2">
                      <CategoryIcon category={cat} />
                      {t(`assistant.category.${cat}`, cat)}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => sweepMutation.mutate()}
            disabled={sweepMutation.isPending}
            className="self-start sm:self-auto sm:shrink-0"
          >
            <ArrowClockwise className="h-4 w-4" />
            {sweepMutation.isPending
              ? t('assistant.actions.sweeping')
              : t('assistant.actions.sweepNow')}
          </Button>
          </div>
        </div>

        <TabsList>
          <TabsTrigger value="open">{t('assistant.tabs.open')}</TabsTrigger>
          <TabsTrigger value="settings">{t('assistant.tabs.settings')}</TabsTrigger>
        </TabsList>

        <TabsContent value="open">
          <SuggestionTable
            query={openQuery}
            emptyTitle={t('assistant.empty.open.title')}
            emptyBody={t('assistant.empty.open.body')}
            buildMenuActions={(s) => {
              // Stop-watching is universal — every check, every kind.
              const stop: RowAction = {
                id: 'stop_watching',
                label: t('assistant.actions.stopWatching'),
                icon: BellSlash,
                onSelect: () => handleStopWatching(s.id),
                disabled: stopWatchingMutation.isPending,
                destructive: true,
              };
              // View details opens the per-row detail page. Only surfaced
              // when the underlying check implements DetailedCheck — the
              // server projects has_details=false for the rest and the SPA
              // hides the menu item rather than rendering a 404 link.
              const viewDetails: RowAction | null = s.has_details
                ? {
                    id: 'view_details',
                    label: t('assistant.actions.viewDetails'),
                    icon: Eye,
                    onSelect: () => navigate(`/orgs/${slug}/assistant/${s.id}`),
                  }
                : null;
              switch (s.action_kind) {
                case 'intent':
                  return [
                    ...(viewDetails ? [viewDetails] : []),
                    {
                      id: 'approve',
                      label: t('assistant.actions.approve'),
                      icon: CheckCircle,
                      onSelect: () => approveMutation.mutate(s.id),
                      disabled: approveMutation.isPending,
                    },
                    stop,
                  ];
                case 'navigate':
                  return [
                    ...(viewDetails ? [viewDetails] : []),
                    {
                      id: 'navigate',
                      label: t('assistant.actions.takeMeThere'),
                      icon: ArrowRight,
                      onSelect: () => {
                        if (s.action_navigate) {
                          navigate(`/orgs/${slug}${s.action_navigate}`);
                        }
                      },
                    },
                    stop,
                  ];
                case 'guidance':
                default:
                  // No primary action button — the row's summary text is
                  // the instruction. View details (when available) +
                  // stop-watching are still available.
                  return [...(viewDetails ? [viewDetails] : []), stop];
              }
            }}
          />
        </TabsContent>

        <TabsContent value="settings">
          <ChecksSettings slug={slug} token={token} categoryFilter={categoryFilter} />
        </TabsContent>
      </Tabs>
    </section>
  );
}

interface ChecksSettingsProps {
  slug: string;
  token: string | null;
  // Lifted to the parent so the page-header Select can drive it from
  // outside this tab content.
  categoryFilter: string;
}

function ChecksSettings({ slug, token, categoryFilter }: ChecksSettingsProps) {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const toast = useToast();

  const checksQuery = useQuery({
    queryKey: ['assistant-checks', slug],
    queryFn: () => listCheckSettings(token, slug),
    enabled: !!token && !!slug,
  });

  const upsertMutation = useMutation({
    mutationFn: (vars: { key: string; enabled: boolean; threshold?: number | null }) =>
      upsertCheckSetting(token, slug, vars.key, {
        enabled: vars.enabled,
        ...(vars.threshold !== undefined ? { threshold: vars.threshold } : {}),
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['assistant-checks', slug] }),
    onError: () => toast.error(t('assistant.toast.settingsFailed')),
  });

  const { page, size, setPage, setSize, offset, limit } = useTablePaginationState({
    defaultSize: 25,
  });

  const checks = useMemo(() => checksQuery.data ?? [], [checksQuery.data]);
  const filteredChecks = useMemo(() => {
    if (categoryFilter === ALL_CATEGORIES_SENTINEL) return checks;
    return checks.filter((c) => c.category === categoryFilter);
  }, [checks, categoryFilter]);
  const pagedChecks = useMemo(
    () => filteredChecks.slice(offset, offset + limit),
    [filteredChecks, offset, limit],
  );

  // When the filter changes from the parent, slide back to page 1 — staying
  // on page 3 after a filter that shrinks the result set below it would
  // show an empty table.
  useEffect(() => {
    setPage(1);
    // setPage from useTablePaginationState is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryFilter]);

  if (checksQuery.isLoading) {
    return <TableSkeleton columns={3} />;
  }
  if (checksQuery.isError) {
    return <LoadError onRetry={() => void checksQuery.refetch()} />;
  }
  if (checks.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">{t('assistant.settings.empty')}</p>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-2xl border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('assistant.settings.columns.check')}</TableHead>
              <TableHead className="hidden sm:table-cell">
                {t('assistant.settings.columns.category')}
              </TableHead>
              <TableHead className="text-right">
                {t('assistant.settings.columns.status')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pagedChecks.map((c) => (
              <CheckRow
                key={c.key}
                check={c}
                disabled={upsertMutation.isPending}
                onToggle={(enabled) =>
                  upsertMutation.mutate({
                    key: c.key,
                    enabled,
                    threshold: c.threshold ?? null,
                  })
                }
              />
            ))}
          </TableBody>
        </Table>
      </div>
      <TablePagination
        total={filteredChecks.length}
        page={page}
        size={size}
        onPageChange={setPage}
        onSizeChange={setSize}
      />
    </div>
  );
}

interface CheckRowProps {
  check: CheckSetting;
  disabled: boolean;
  onToggle: (enabled: boolean) => void;
}

function CheckRow({ check, disabled, onToggle }: CheckRowProps) {
  const { t } = useTranslation('common');
  const category = check.category ?? '';
  // Translated static label (no count). Falls back to the backend's
  // English Check.Title() for any check key the SPA doesn't know yet —
  // so a new backend-side check ships visible even before its locale
  // entries land.
  const label = t(`assistant.checks.${check.key}.label`, { defaultValue: check.title });
  return (
    <TableRow>
      <TableCell>
        <div className="flex flex-col gap-1.5">
          <span className="font-medium text-foreground">{label}</span>
          <div className="flex flex-wrap items-center gap-2 sm:hidden">
            {category && (
              <Badge variant="outline" className="text-xs">
                {t(`assistant.category.${category}`)}
              </Badge>
            )}
          </div>
          {check.threshold !== undefined && check.threshold !== null && (
            <span className="text-xs text-muted-foreground">
              {t('assistant.settings.thresholdValue', { count: check.threshold })}
            </span>
          )}
        </div>
      </TableCell>
      <TableCell className="hidden sm:table-cell">
        {category && (
          <Badge variant="outline" className="text-xs">
            {t(`assistant.category.${category}`)}
          </Badge>
        )}
      </TableCell>
      <TableCell className="text-right">
        <Button
          variant={check.enabled ? 'default' : 'outline'}
          size="sm"
          onClick={() => onToggle(!check.enabled)}
          disabled={disabled}
          aria-pressed={check.enabled}
        >
          {check.enabled
            ? t('assistant.settings.enabled')
            : t('assistant.settings.disabled')}
        </Button>
      </TableCell>
    </TableRow>
  );
}

interface SuggestionTableProps {
  query: {
    isLoading: boolean;
    isError: boolean;
    data: Suggestion[] | undefined;
  };
  emptyTitle: string;
  emptyBody: string;
  // Per-row action set rendered through the shared RowActions dropdown
  // (3-dot menu). Each tab passes a different set: Open has approve +
  // reject; Rejected has restore.
  buildMenuActions: (s: Suggestion) => RowAction[];
}

function SuggestionTable({
  query,
  emptyTitle,
  emptyBody,
  buildMenuActions,
}: SuggestionTableProps) {
  const { t } = useTranslation('common');
  const { page, size, setPage, setSize, offset, limit } = useTablePaginationState({
    defaultSize: 25,
  });
  const rows = useMemo(() => query.data ?? [], [query.data]);
  const paged = useMemo(() => rows.slice(offset, offset + limit), [rows, offset, limit]);

  if (query.isLoading) {
    return <TableSkeleton columns={4} />;
  }
  if (query.isError) {
    return <LoadError />;
  }
  if (rows.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-border p-8 text-center">
        <p className="font-heading text-lg font-semibold text-foreground">{emptyTitle}</p>
        <p className="mt-1 text-sm text-muted-foreground">{emptyBody}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-2xl border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('assistant.open.columns.suggestion')}</TableHead>
              <TableHead className="hidden sm:table-cell">
                {t('assistant.open.columns.category')}
              </TableHead>
              <TableHead className="hidden text-right sm:table-cell">
                {t('assistant.open.columns.affected')}
              </TableHead>
              <TableHead className="text-right">
                {t('assistant.open.columns.actions')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paged.map((s) => (
              <SuggestionRow key={s.id} suggestion={s} menuActions={buildMenuActions(s)} />
            ))}
          </TableBody>
        </Table>
      </div>
      <TablePagination
        total={rows.length}
        page={page}
        size={size}
        onPageChange={setPage}
        onSizeChange={setSize}
      />
    </div>
  );
}

interface SuggestionRowProps {
  suggestion: Suggestion;
  menuActions: RowAction[];
}

function SuggestionRow({ suggestion, menuActions }: SuggestionRowProps) {
  const { t } = useTranslation('common');
  // Empty for a rule-origin suggestion (no check key to categorise), in which
  // case the badge is omitted rather than showing a placeholder.
  const category = suggestion.category ?? '';
  // Title + summary are translated client-side from check_key so we don't
  // re-sweep when copy changes and so per-check phrasing can be pluralised
  // (i18next picks _one / _other based on `count`). Fall back to the
  // backend-rendered text for unknown / rule-origin keys.
  const title = suggestion.check_key
    ? t(`assistant.checks.${suggestion.check_key}.title`, {
        count: suggestion.blast_radius.row_count,
        defaultValue: suggestion.title,
      })
    : suggestion.title;
  const summary = suggestion.check_key
    ? t(`assistant.checks.${suggestion.check_key}.summary`, {
        defaultValue: suggestion.summary,
      })
    : suggestion.summary;
  return (
    <TableRow>
      <TableCell>
        <div className="flex flex-col gap-1">
          <span className="font-medium leading-snug text-foreground">{title}</span>
          <span className="text-sm leading-snug text-muted-foreground">{summary}</span>
          <div className="flex flex-wrap items-center gap-2 pt-1 sm:hidden">
            {category && (
              <Badge variant="outline" className="text-xs">
                {t(`assistant.category.${category}`)}
              </Badge>
            )}
            {suggestion.blast_radius.row_count > 0 && (
              <Badge variant="secondary" className="text-xs">
                {t('assistant.blastRadius.rows', { count: suggestion.blast_radius.row_count })}
              </Badge>
            )}
          </div>
        </div>
      </TableCell>
      <TableCell className="hidden sm:table-cell">
        {category && (
          <Badge variant="outline" className="text-xs">
            {t(`assistant.category.${category}`)}
          </Badge>
        )}
      </TableCell>
      <TableCell className="hidden text-right sm:table-cell">
        <span className="text-sm text-muted-foreground">
          {suggestion.blast_radius.row_count}
        </span>
      </TableCell>
      <TableCell className="text-right">
        <RowActions menuActions={menuActions} />
      </TableCell>
    </TableRow>
  );
}
